import { useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetWHOrdersNewQuery } from "store/api/warehouseManagerApi"
import { ApiTypes, UITypes } from "types"

import { convertWHManagerOrdersNewToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { PackingDeviceModal } from "modals/PackingDeviceModal"
import { ShippingModal } from "modals/ShippingModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import TableWrapper from "components/table/TableWrapper"
import { WidgetOrders } from "components/widgets/WidgetOrders"
import { WidgetWarehouse } from "components/widgets/WidgetWarehouse"

import { warehouseOrderTableHeaderCells } from "./warehouseOrderTableHeaderCells"

import styles from "./index.module.scss"

export const Orders = () => {
  const [triggerGetWHOrdersNew, { isFetching, data }] = useLazyGetWHOrdersNewQuery()

  const { urlQueryParams } = useUrlQueryParams()
  const [isFirstRender, setFirstRender] = useState(true)
  const [toBePackedOrder, setToBePackedOrder] = useState<ApiTypes.Model.WHManagerOrderNew | null>(null)
  const [toBeShippingOrder, setToBeShippingOrder] = useState<ApiTypes.Model.WHManagerOrderNew | null>(null)
  const [updateIndex, setUpdateIndex] = useState(0)

  const sendRequest = async (params: Record<string, string>) => {
    try {
      const res = await triggerGetWHOrdersNew(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const fetchData = () => {
    sendRequest(urlQueryParams)
  }

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    fetchData()
  }, [urlQueryParams, isFirstRender])

  const getOrderItem = (id: number) => {
    const order = data?.data?.find(order => order.items.some(item => item.id === id))

    if (!order) return null

    return {
      ...order,
      items: order.items.filter(item => item.id === id),
    }
  }

  const onPackingClick = (id: number) => {
    const order = getOrderItem(id)
    if (!order) return

    setToBePackedOrder(order)
  }

  const onShippingClick = (id: number) => {
    const order = getOrderItem(id)
    if (!order) return

    setToBeShippingOrder(order)
  }

  const getRowActions = (rowData: UITypes.TableBodyRow) => {
    const status = rowData.item_status.value
    const item = rowData.rowSettings.item[0]
    return {
      className: styles.tableActionsWrapper,
      actionButtons: [
        {
          icon: item.product_type === "cwa" ? "Shipping" : "Packing",
          hidden: status !== "confirmed",
          handler: () => onPackingClick(rowData.item_id.value as number),
        },
        {
          icon: "Shipping",
          hidden: status !== "packed",
          handler: () => onShippingClick(rowData.item_id.value as number),
        },
      ],
    }
  }

  const handleSuccess = () => {
    setToBePackedOrder(null)
    setToBeShippingOrder(null)
    setUpdateIndex(prev => prev + 1)
  }

  return (
    <>
      <div className={styles.widgetsWrapper}>
        <WidgetOrders updateIndex={updateIndex} />
        <WidgetWarehouse updateIndex={updateIndex} />
      </div>
      <ContentWrapper title="Orders">
        <TableWrapper
          headerCells={warehouseOrderTableHeaderCells}
          isLoading={isFetching}
          data={convertWHManagerOrdersNewToTableRows(data?.data)}
          getRowActions={getRowActions}
          rowsExpanded
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
      {toBePackedOrder && (
        <PackingDeviceModal
          order={toBePackedOrder}
          onCancel={() => setToBePackedOrder(null)}
          onSuccess={handleSuccess}
        />
      )}
      {toBeShippingOrder && (
        <ShippingModal
          onCancel={() => setToBeShippingOrder(null)}
          onSuccess={handleSuccess}
          orderItemId={toBeShippingOrder.items[0].id}
        />
      )}
    </>
  )
}
